import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentGame } from '@/store/gameSlice';
import type { Resources } from '@/types';
import './StorePanel.css';

// Resource icon cache configuration (reuse from PlayerPanel)
const RESOURCE_CACHE_PREFIX = 'rush-icon-';
const RESOURCE_CACHE_VERSION = '2'; // Incremented to clear corrupted data URI cache
const RESOURCE_CACHE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// Resource type to icon filename mapping
const RESOURCE_ICON_MAP: Record<string, string> = {
  energy: 'energy-8bit.png',
  minerals: 'minerals-8bit.png',
  food: 'food-8bit.png',
  oxygen: 'oxygen-8bit.png',
  hydrogen: 'hydrogen-8bit.png',
  water: 'water-8bit.png',
  helium3: 'helium3-8bit.png',
  rovers: 'rovers-8bit.png',
  money: 'money-8bit.png',
};

/**
 * Get cached resource icon
 */
const getCachedResourceIcon = (iconName: string): string | null => {
  try {
    const cacheKey = `${RESOURCE_CACHE_PREFIX}${iconName}-v${RESOURCE_CACHE_VERSION}`;
    const cached = localStorage.getItem(cacheKey);
    
    if (!cached) {
      return null;
    }
    
    const { dataUri, timestamp } = JSON.parse(cached);
    const now = Date.now();
    
    if (now - timestamp > RESOURCE_CACHE_EXPIRY_MS) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    return dataUri;
  } catch (error) {
    return null;
  }
};

/**
 * Cache resource icon
 */
const cacheResourceIcon = (iconName: string, dataUri: string): void => {
  try {
    const cacheKey = `${RESOURCE_CACHE_PREFIX}${iconName}-v${RESOURCE_CACHE_VERSION}`;
    const cacheData = {
      dataUri,
      timestamp: Date.now(),
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    // localStorage might be full
  }
};

/**
 * StorePanel - Displays Base/Store inventory and prices
 * 
 * In the original M.U.L.E., the Store had inventory that players could see.
 * This panel shows what resources the Store has available and current market prices.
 * Prices fluctuate based on supply: more resources = lower price, less = higher price.
 */
export default function StorePanel() {
  const game = useSelector(selectCurrentGame);
  const [resourceIcons, setResourceIcons] = useState<Record<string, string>>({});
  
  // Store inventory from game state
  // TODO: Add store inventory to GameState type when backend implements it
  const storeInventory: Resources = (game as any)?.store?.inventory || {
    energy: 0,
    minerals: 0,
    food: 0,
    oxygen: 0,
    hydrogen: 0,
    water: 0,
    helium3: 0,
    rovers: 0,
  };
  
  const storeMoney = (game as any)?.store?.money || 0;
  
  // Market prices from game state
  // Prices fluctuate: more inventory = lower price, less inventory = higher price
  const marketPrices: Record<string, number> = game?.market?.prices || {
    energy: 35,
    minerals: 50,
    food: 30,
    oxygen: 25,
    hydrogen: 30,
    water: 20,
    helium3: 75,
    rovers: 200,
  };
  
  // Load resource icons on mount
  useEffect(() => {
    const loadResourceIcons = async () => {
      const resourceTypes = Object.keys(RESOURCE_ICON_MAP);
      const loadedIcons: Record<string, string> = {};
      
      for (const resourceType of resourceTypes) {
        const iconName = RESOURCE_ICON_MAP[resourceType];
        
        // Check cache first
        const cachedDataUri = getCachedResourceIcon(iconName);
        
        if (cachedDataUri) {
          loadedIcons[resourceType] = cachedDataUri;
          continue;
        }
        
        // Cache miss - fetch from get_icon API (S3 via Lambda)
        try {
          const { apiClient } = await import('@/services/api');
          const dataUrl = await apiClient.getIcon(iconName);
          
          loadedIcons[resourceType] = dataUrl;
          cacheResourceIcon(iconName, dataUrl);
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`✅ Fetched and cached resource icon from API: ${iconName}`);
          }
        } catch (error) {
          console.error(`Error loading resource icon ${iconName}:`, error);
        }
      }
      
      setResourceIcons(loadedIcons);
    };
    
    loadResourceIcons();
  }, []);

  return (
    <div className="store-panel">
      <h3 className="store-panel-title">BASE / STORE</h3>
      <div className="store-inventory">
        <div className="store-item" data-tooltip={`Energy: ${storeInventory.energy} units | ${marketPrices.energy} CT/unit`}>
          {resourceIcons.energy ? (
            <img src={resourceIcons.energy} alt="Energy" className="store-icon-small" />
          ) : (
            <span className="store-icon-fallback">🔋</span>
          )}
          <div className="store-info">
            <span className="store-value">{storeInventory.energy}</span>
            <span className="store-price">{marketPrices.energy} CT</span>
          </div>
        </div>
        <div className="store-item" data-tooltip={`Minerals: ${storeInventory.minerals} units | ${marketPrices.minerals} CT/unit`}>
          {resourceIcons.minerals ? (
            <img src={resourceIcons.minerals} alt="Minerals" className="store-icon-small" />
          ) : (
            <span className="store-icon-fallback">⛏️</span>
          )}
          <div className="store-info">
            <span className="store-value">{storeInventory.minerals}</span>
            <span className="store-price">{marketPrices.minerals} CT</span>
          </div>
        </div>
        <div className="store-item" data-tooltip={`Food: ${storeInventory.food ?? 0} units | ${marketPrices.food} CT/unit`}>
          {resourceIcons.food ? (
            <img src={resourceIcons.food} alt="Food" className="store-icon-small" />
          ) : (
            <span className="store-icon-fallback">🌾</span>
          )}
          <div className="store-info">
            <span className="store-value">{storeInventory.food ?? 0}</span>
            <span className="store-price">{marketPrices.food} CT</span>
          </div>
        </div>
        <div className="store-item" data-tooltip={`Oxygen: ${storeInventory.oxygen ?? 0} units | ${marketPrices.oxygen} CT/unit`}>
          {resourceIcons.oxygen ? (
            <img src={resourceIcons.oxygen} alt="Oxygen" className="store-icon-small" />
          ) : (
            <span className="store-icon-fallback">💨</span>
          )}
          <div className="store-info">
            <span className="store-value">{storeInventory.oxygen ?? 0}</span>
            <span className="store-price">{marketPrices.oxygen} CT</span>
          </div>
        </div>
        <div className="store-item" data-tooltip={`Hydrogen: ${storeInventory.hydrogen ?? 0} units | ${marketPrices.hydrogen} CT/unit`}>
          {resourceIcons.hydrogen ? (
            <img src={resourceIcons.hydrogen} alt="Hydrogen" className="store-icon-small" />
          ) : (
            <span className="store-icon-fallback">☁️</span>
          )}
          <div className="store-info">
            <span className="store-value">{storeInventory.hydrogen ?? 0}</span>
            <span className="store-price">{marketPrices.hydrogen} CT</span>
          </div>
        </div>
        <div className="store-item" data-tooltip={`Water: ${storeInventory.water ?? 0} units | ${marketPrices.water} CT/unit`}>
          {resourceIcons.water ? (
            <img src={resourceIcons.water} alt="Water" className="store-icon-small" />
          ) : (
            <span className="store-icon-fallback">💧</span>
          )}
          <div className="store-info">
            <span className="store-value">{storeInventory.water ?? 0}</span>
            <span className="store-price">{marketPrices.water} CT</span>
          </div>
        </div>
        <div className="store-item" data-tooltip={`Helium-3: ${storeInventory.helium3 ?? 0} units | ${marketPrices.helium3} CT/unit`}>
          {resourceIcons.helium3 ? (
            <img src={resourceIcons.helium3} alt="Helium-3" className="store-icon-small" />
          ) : (
            <span className="store-icon-fallback">✨</span>
          )}
          <div className="store-info">
            <span className="store-value">{storeInventory.helium3 ?? 0}</span>
            <span className="store-price">{marketPrices.helium3} CT</span>
          </div>
        </div>
        <div className="store-item" data-tooltip={`Rovers: ${storeInventory.rovers ?? 0} units | ${marketPrices.rovers} CT/unit`}>
          {resourceIcons.rovers ? (
            <img src={resourceIcons.rovers} alt="Rovers" className="store-icon-small" />
          ) : (
            <span className="store-icon-fallback">🚛</span>
          )}
          <div className="store-info">
            <span className="store-value">{storeInventory.rovers ?? 0}</span>
            <span className="store-price">{marketPrices.rovers} CT</span>
          </div>
        </div>
        <div className="store-item store-money" data-tooltip={`Cyber Tokens: ${storeMoney.toLocaleString()} CT`}>
          {resourceIcons.money ? (
            <img src={resourceIcons.money} alt="Cyber Tokens" className="store-icon-small" />
          ) : (
            <span className="store-icon-fallback">🪙</span>
          )}
          <div className="store-info">
            <span className="store-value">{storeMoney.toLocaleString()}</span>
            <span className="store-price">CT</span>
          </div>
        </div>
      </div>
    </div>
  );
}

